import { createAppStore } from '@mobileStore/createAppStore';
import {
  CustomerDetailState,
  Financials,
  ICustomerDetails,
} from '@typeDefinitions/customerTypes';

export type OrderHistoryModel = {
  agent: string | number;
  cost: number;
  customerId: number;
  destinationCity: string;
  destinationState: string;
  dropoff: string;
  numLoads: number;
  orderId: number;
  originCity: string;
  originState: string;
  pickUp: string;
  proNumber: string | null;
  profit: number;
  referenceNumber: string | null;
  revenue: number;
  status: string;
  stopDropOffNumber: string;
  stopPickupNumber: string;
  trackingNumber: string;
  loadProduct: string;
};

interface HistoryFilter {
  field: string;
  filterType:
    | 'EQUALS'
    | 'NOT_EQUALS'
    | 'GREATER_THAN'
    | 'GREATER_THAN_OR_EQUALS'
    | 'LESS_THAN'
    | 'LESS_THAN_OR_EQUALS'
    | 'CONTAINS'
    | 'NOT_CONTAINS'
    | 'STARTS_WITH'
    | 'ENDS_WITH';
  values: (string | number)[];
}

interface Sort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface HistoryGridPayload {
  filters: HistoryFilter[];
  sort: Sort;
  pageNumber: number;
  pageSize: number;
}

export interface MobileCustomerDetailState extends CustomerDetailState {
  orderHistory: {
    data: OrderHistoryModel[];
    totalCount: number;
    isLoading: boolean;
    pageNumber: number;
    hasMore: boolean;
  };
}

export const INITIAL_CUSTOMER_DETAILS: ICustomerDetails = {
  id: null,
  creditInsuranceId: null,
  creditInsuranceCoverageType: null,
  creditInsuranceCoverageTypeName: null,
  creditInsurancePolicyDate: null,
  createdDate: null,
  name: null,
  taxId: null,
  emailDomain: null,
  physicalAddress1: null,
  physicalAddress2: null,
  physicalCity: null,
  physicalState: null,
  physicalZip: null,
  mailingAddress1: null,
  mailingAddress2: null,
  mailingCity: null,
  mailingState: null,
  mailingZip: null,
  mailingCountryCode: null,
  creditLimit: 0,
  insuredFor: 0,
  accountManagerName: null,
  accountManagerEmail: null,
  salesManagerName: null,
  invoiceMethod: null,
  billingInstructions: null,
  bolInstructions: null,
  status: 'Customer',
  subStatus: null,
  paymentTerms: null,
  collectionTerms: null,
  userName: null,
  password: null,
  accountingSysId: null,
  loadInstructions: null,
  requireEmptyBeforeProcessingLoad: false,
  optOutBalanceEmail: false,
  optOutPastDueDateBalanceEmail: false,
  allowAutoDispatch: false,
  enableLTL: false,
  enableCSP: false,
  ediMapping: null,
  verified: undefined,
  invoicePaperworkRequired: undefined,
  alwaysTrackLoads: false,
  isPublic: false,
  customerExists: false,
  chargeCreditCardFee: null,
  trackingXId: null,
  accountManagerOfficeId: null,
  salesManagerOfficeId: null,
  accountManagerId: null,
  trackingServiceId: null,
  salesManagerId: null,
  prospectId: null,
  collectionsUserId: null,
  commissionDiscount: null,
  accountingSysBalance: null,
  ediTemplateId: null,
  parentId: null,
  needsPreapproval: undefined,
  outstandingOrders: null,
  orderSummary: null,
  quotes: [],
  comments: [],
  contacts: [],
  requests: [],
  groups: [],
  financials: {} as Financials,
  collectionComments: [],
  trackings: null,
  metadata: [],
  requiredDocumentTypes: [],
  atgLiability: null,
  agentLiability: null,
  agentLiabilityNote: null,
  agentLiabilityEffectiveDate: null,
  claimedBy: null,
  claimedById: null,
  pcMilerVersion: null,
  collectionTierId: null,
  nextActionDate: null,
  keyReasonId: null,
  insurance: null,
  ltlCostPlus: null,
  businessTypeName: null,
  businessTypeCode: null,
  businessTypeId: null,
  businessTypeDisplayName: null,
  loadPending: false,
  paymentType: 0,
  requestedCustomerDoNotSolicit: null,
  isApprovedCustomerDoNotSolicit: null,
  isCustomerCentralized: null,
  seasonality: null,
  approvedCustomerDoNotSolicitDate: null,
  holdRetraction: false,
  revisitRetractionDate: null,
  exceptionReason: null,
  seasonalityComment: '',
  trackingVisibilityProvider: null,
  capacityGroupId: null,
  defaultLogoUrl: null,
  outstandingBalance: 0,
  physicalCountryCode: null,
  accountManagerPhone: null,
  accountManagerOfficeName: null,
  accountManagerOfficeAccountingGroupName: null,
  salesManagerEmail: null,
  salesManagerPhone: null,
  salesManagerOfficeName: null,
  salesManagerOfficeAccountingGroupName: null,
  customerTier: null,
  isPublicEditable: false,
  negativeCreditAllowance: null,
  mcNumber: null,
  isAuthorityFreightBroker: null,
  isAuthorityCarrier: null,
  isAuthorityFreightForwarder: null,
  isAuthorityDirectShipper: null,
  assetBased: null,
  isCrossBorder: null,
};

const [customerDetailsStore, setCustomerDetailsStore] =
  createAppStore<MobileCustomerDetailState>(
    {
      isLoading: false,
      isError: false,
      errorMessage: '',
      customer: INITIAL_CUSTOMER_DETAILS,
      assignOfficeGroup: [],
      customerFlagReviewModalModeAndLoading: {
        mode: 'add',
        loading: false,
      },
      orderHistory: {
        data: [],
        totalCount: 0,
        isLoading: false,
        pageNumber: 1,
        hasMore: true,
      },
    },
    'customerDetailsStore',
  );

export { customerDetailsStore, setCustomerDetailsStore };
